import * as React from 'react';

import ReactMapboxGl, { Marker } from 'react-mapbox-gl';
import { connect } from 'react-redux';
import { parse } from 'query-string';

import { userId$ } from '../../selectors/mapField';

import MapTile from './MapTile';
import DrawnMap from './DrawnMap';

import * as MapActions from '../../actions/map';
import * as MapFieldListCreators from '../../creators/mapFieldList';
import * as Const from '../../constants';
import * as MapFieldListSelector from '../../selectors/mapFieldList';
import * as MapFieldActions from '../../actions/mapField';
import * as FieldConst from './constants';

import * as marker from '../../assets/image/placeholder.svg';

import MapFieldList from './MapFieldList';
import MapControlls from './MapControlls';

import State from '../../interfaces/states/State';

import MapFieldListInterface from '../../interfaces/MapFieldList';

import './style.scss';

const Map = ReactMapboxGl({
  accessToken: Const.MAP_KEY,
});

interface DispatchProps {
  fetchMapHide: () => void;
  fetchMapFieldList: (id: string) => void;
  setUserId: (id: string) => void;
}

interface OwnProps {
  location: Location;
  mapFieldListOrder: number[];
  mapFieldListById: Record<number, MapFieldListInterface>;
  isError: boolean;
  isLoading: boolean;
  userId: string;
  currentId: number;
  currentDrawLayer: number[][];
  currentCenter: [number, number];
  currentSnapTile: any;
}

interface Props extends DispatchProps, OwnProps {}

const MapWrapper = (props: Props) => {
  const [opacity, setOpacity] = React.useState('0.4');
  const [isControllsShowed, setControlls] = React.useState(false);
  const [fillColor, setFillColor] = React.useState('red');
  const [mapStyle, setMapStyle] = React.useState(Const.DEFAULT_MAP_STYLE);
  const [zoom, setZoom] = React.useState(15);

  const {
    fetchMapFieldList,
    setUserId,
    mapFieldListOrder,
    mapFieldListById,
    isError,
    isLoading,
    userId,
    currentCenter,
    currentSnapTile,
    currentDrawLayer,
  } = props;

  const values: any = parse(location.search);

  React.useEffect(() => {
    fetchMapFieldList(values.id);
    setUserId(values.id);
  }, []);

  const setMapControlls = React.useCallback(() => {
    setControlls(!isControllsShowed);
  }, [isControllsShowed]);

  const geoCoordinates = {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: currentDrawLayer,
      },
    },
  };

  const sourceOptions = {
    'type': 'raster',
    'tiles': [currentSnapTile],
    'tileSize': 512,
  };

  const onHandleZoom = React.useCallback((zoom) => setZoom(zoom.getZoom()), [zoom]);

  const onIncreaseZoom = () => setZoom(zoom + 1.5);

  const onDecreaseZoom = () => setZoom(zoom - 1.5);

  return(
    <section
      className='map_wrapper d-flex justify-center align-center'>
      <div className='map_content d-flex justify-around align-flex-start'>
        {
          isControllsShowed && <MapControlls
            setMapStyle={setMapStyle}
            fillColor={fillColor}
            setFillColor={setFillColor}
            setOpacity={setOpacity}
            opacity={opacity} />
        }
        <Map
          onZoom={onHandleZoom}
          // @ts-ignore
          center={currentCenter ? currentCenter : FieldConst.DEFAULT_COORDINATES}
          zoom={[zoom]}
          style={mapStyle}
          containerStyle={{
            height: '100vh',
            width: 'calc(100vw - 281px)',
          }} >
          { !!currentSnapTile && <MapTile sourceOptions={sourceOptions} /> }
          { !!currentDrawLayer && <DrawnMap
            fillColor={fillColor}
            opacity={opacity}
            geoCoordinates={geoCoordinates} /> }
          <Marker
            className='map_marker'
            style={{ height: 80, width: 40 }}
            coordinates={currentCenter ? currentCenter : FieldConst.DEFAULT_COORDINATES}
            anchor='center'>
            <img src={marker} />
          </Marker>
        </Map>

        <div className='container-btn-zoom'>
          <button
            onClick={onIncreaseZoom}
            className='btn-zoom btn-increase'>
            {'+'}
          </button>
          <br />
          <button
            onClick={onDecreaseZoom}
            className='btn-zoom btn-decrease'>
            {'-'}
          </button>
        </div>

        <MapFieldList
          setControlls={setMapControlls}
          userId={userId}
          mapFieldListOrder={mapFieldListOrder}
          mapFieldListById={mapFieldListById}
          isError={isError}
          isLoading={isLoading} />
      </div>
    </section>
  );
};

const mapStateToProps = (state: State) => {
  return {
    userId: userId$(state),
    isError: MapFieldListSelector.isError$(state),
    isLoading: MapFieldListSelector.isLoading$(state),
    mapFieldListById: MapFieldListSelector.mapFieldListById$(state),
    mapFieldListOrder: MapFieldListSelector.mapFieldListOrder$(state),
    currentId: MapFieldListSelector.currentId$(state),
    currentDrawLayer: state.mapFieldList.currentDrawLayer,
    currentCenter: state.mapFieldList.currentCenter,
    currentSnapTile: state.mapField.currentSnapTile,
  };
};

const mergeProps = (stateProps, dispatchProps: DispatchProps, props: OwnProps): Props => {
  return {
    ...stateProps,
    fetchMapHide: dispatchProps.fetchMapHide,
    fetchMapFieldList: dispatchProps.fetchMapFieldList,
    setUserId: dispatchProps.setUserId,
    ...props,
  };
};

export default connect(
  mapStateToProps, {
    fetchMapHide: MapActions.fetchMapHide,
    fetchMapFieldList: MapFieldListCreators.fetchMapFieldList,
    setUserId: MapFieldActions.setUserId,
  }, mergeProps
)(MapWrapper);
